/**
 * Engagement module DTOs (§12.1, §12.4, §15.2): task completion, channel linking, referral redirects and the
 * admin platform / task catalogue. Qualification stays pending until evidence or conversion exists.
 */
import { z } from 'zod';
import { E164Phone, HttpsUrl, IsoDateTime, SocialHandle, Ulid } from './common.js';
import { EngagementTaskType, SocialPlatform, TaskCompletionStatus } from './enums.js';
import { ReferralCode } from './ids.js';
import { EngagementTaskConfigSchema, PlatformConfigSchema } from './lottery-config.js';
export const CompleteTaskRequest = z.object({
    /** The participant's entry (identifies lottery and profile without an account). */
    entryId: Ulid,
    /** Follow / join: participant attestation (§12.4). */
    attestation: z.boolean().optional(),
    platform: SocialPlatform.optional(),
    /** Share: the share code that was generated for this participant. */
    shareCode: ReferralCode.optional(),
    /** Link channel: the additional handle. */
    channel: z
        .discriminatedUnion('kind', [
        z.object({ kind: z.literal('WHATSAPP'), value: E164Phone }),
        z.object({ kind: z.literal('SOCIAL_HANDLE'), platform: SocialPlatform, value: SocialHandle }),
    ])
        .optional(),
    consentTextVersion: z.string().min(1).max(32),
});
export const TaskCompletionDto = z.object({
    completionId: Ulid,
    taskId: Ulid,
    type: EngagementTaskType,
    status: TaskCompletionStatus,
    bonusEntriesAwarded: z.number().int(),
    /** Real counts only (§12.4 progress). */
    bonusEntriesTotal: z.number().int(),
    bonusEntryCap: z.number().int(),
    reason: z.string().optional(),
    createdAt: IsoDateTime,
    qualifiedAt: IsoDateTime.nullable(),
});
export const LinkChannelRequest = z.object({
    entryId: Ulid,
    channel: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('WHATSAPP'), value: E164Phone }),
        z.object({ kind: z.literal('SOCIAL_HANDLE'), platform: SocialPlatform, value: SocialHandle }),
    ]),
    consentTextVersion: z.string().min(1).max(32),
});
export const ChannelDto = z.object({
    channelId: Ulid,
    platform: SocialPlatform,
    valueMasked: z.string(),
    linkedAt: IsoDateTime,
    primary: z.boolean(),
});
/** `GET /v1/r/:code` resolves a referral / share code into the lottery link with source tags. */
export const ReferralResolutionDto = z.object({
    code: ReferralCode,
    lotterySlug: z.string(),
    redirectUrl: z.string(),
    sourcePlatform: SocialPlatform.optional(),
    campaign: z.record(z.string(), z.string()).optional(),
});
export const ShareCardDto = z.object({
    entryId: Ulid,
    imageUrl: z.string(),
    shareUrl: z.string(),
    referralCode: ReferralCode,
    /** Pre-filled share text (no contact details). */
    text: z.string(),
});
// --- admin -----------------------------------------------------------------------------------------------
export const UpsertPlatformRequest = PlatformConfigSchema.extend({ lotteryId: Ulid });
export const PlatformAdminDto = PlatformConfigSchema.extend({
    platformConfigId: Ulid,
    lotteryId: Ulid,
    accountUrl: HttpsUrl,
    updatedAt: IsoDateTime,
});
export const UpsertEngagementTaskRequest = EngagementTaskConfigSchema.omit({ taskId: true }).extend({
    lotteryId: Ulid,
    taskId: Ulid.optional(),
});
export const EngagementTaskAdminDto = EngagementTaskConfigSchema.extend({
    lotteryId: Ulid,
    completions: z.object({
        pending: z.number().int(),
        qualified: z.number().int(),
        rejected: z.number().int(),
    }),
    updatedAt: IsoDateTime,
});
export const ReviewTaskCompletionRequest = z.object({
    status: z.enum(['QUALIFIED', 'REJECTED']),
    reason: z.string().max(2000).optional(),
});
// ---------------------------------------------------------------------------------------------------------
// Extensions (engagement agent, D-1301…)
// ---------------------------------------------------------------------------------------------------------
/** `POST /v1/participants/me/channels` answer: the linked channel plus the real bonus effect (§12.1, §12.4). */
export const LinkChannelResultDto = ChannelDto.extend({
    /** Present when a LINK_CHANNEL task is published for the lottery and the link qualified. */
    completionId: Ulid.optional(),
    bonusEntriesAwarded: z.number().int(),
    bonusEntriesTotal: z.number().int(),
    bonusEntryCap: z.number().int(),
    /** False when the platform was already linked (+1 only once per platform, §12.4). */
    newlyLinked: z.boolean(),
});
/** `GET /v1/l/:slug/tasks` — the published tasks (same shape as the receipt rows, without participant progress). */
export const PublicTaskListDto = z.object({
    lotteryId: Ulid,
    bonusEntryCap: z.number().int(),
    tasks: z.array(z.object({
        taskId: Ulid,
        type: EngagementTaskType,
        title: z.string(),
        consentText: z.string(),
        bonusEntries: z.number().int(),
        platform: SocialPlatform.optional(),
        destinationUrl: z.string().optional(),
        cap: z.number().int().optional(),
    })),
});
//# sourceMappingURL=engagement.js.map